import { t as tr } from '@hana/i18n';
import './OpsCost.i18n';
import { useMemo, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import {
  IconAlertTriangle, IconArrowRight, IconBuilding, IconCalendar, IconCash, IconCheck, IconChevronDown, IconChevronLeft, IconChevronRight, IconClock, IconDownload, IconExternalLink,
  IconFileText, IconFilter, IconFilterOff, IconHistory, IconBell, IconLock, IconReceipt, IconSearch, IconTicket, IconTruck, IconUpload, IconX, IconCoin,
} from '@tabler/icons-react';
import { Page, PageHeader, useLayout, useShell } from '@hana/shell';
import { OpsTripDrawer } from './OpsTripDrawer';
import { Button, Skeleton, StateView, useToast } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from './access';
import {
  ALL_TRIPS, CARGO_LABEL, CATS, COLS, FEE_NAME, HAS_PHOTO, MASK, NO_FILTER, STATUS_LABEL, catTotals, chipPass, dayLabel, driverTotals, fullVnd, overInCols, overTip, passFilter, periodLabel, sumCats, th, tripsInPeriod,
  type CargoKind, type OpsChip, type OpsFilter, type OpsTab, type OpsTrip, type Period, type TripStatus,
} from './opscostData';
import s from './opscost.module.css';
import p from './opscostPad.module.css';

// Mint-OpsCost (1536): chi phí dùng chung theo mảng · Trucking. Tab Theo chuyến (mặc định) · Theo loại phí · Theo tài xế.
// TIỀN → sau quyền `money` (không quyền: số che ••••, ghi chú đầu bảng). Chỉ đọc; nút ghi báo toast. Đầu trang dùng PageHeader của shell (thu gọn gom bằng CW, issue 371).

const STATUS_DOT: Readonly<Record<TripStatus, string>> = { pnd: 'var(--d-amber)', apv: 'var(--d-blue)', ok: 'var(--ok-dot)', set: 'var(--d-violet)' };
const CARGO_DOT: Readonly<Record<CargoKind, string>> = { RF: 'var(--d-violet)', DC: 'var(--d-blue)', LCL: 'var(--ok)' };
const CHIPS: readonly { k: OpsChip; ic: ReactNode; tip: string; short: string }[] = [
  { k: 'pnd', ic: <IconFileText size={13} aria-hidden="true" />, tip: 'Chờ chứng từ: còn dòng chờ hoá đơn hoặc thiếu ảnh', short: 'chờ chứng từ' },
  { k: 'apv', ic: <IconClock size={13} aria-hidden="true" />, tip: 'Chờ duyệt', short: 'chờ duyệt' },
  { k: 'over', ic: <IconAlertTriangle size={13} aria-hidden="true" />, tip: 'Có dòng vượt định mức', short: 'vượt định mức' },
  { k: 'unset', ic: <IconCash size={13} aria-hidden="true" />, tip: 'Còn khoản nhân viên trả chưa quyết toán', short: 'chưa quyết toán' },
];
const TABS: readonly (readonly [OpsTab, string])[] = [['row', 'Theo chuyến'], ['cat', 'Theo loại phí'], ['ppl', 'Theo tài xế']];
const AMT_W = 90;
const GRID = `44px 146px 100px 92px minmax(0, 1fr) ${COLS.map(() => `${AMT_W}px`).join(' ')} 92px 116px`;
const TH_COLS: readonly (readonly [string, boolean, string])[] = [
  ['Lệnh', false, ''], ['Tài xế', false, ''], ['Xe', false, ''], ['Tuyến', false, ''],
  ...COLS.map((c) => [c.label, true, CATS.filter((x) => c.cats.includes(x.key)).map((x) => `${x.name} (${x.codes.join(', ')})`).join(' · ') + ' · nghìn ₫'] as const),
  ['Tổng', true, 'Tổng chi · nghìn ₫'], ['Trạng thái', false, ''],
];

type FKey = 'ppl' | 'veh' | 'status';
const initials = (n: string) => n.replace(/[^A-ZÀ-Ỹ]/g, '').slice(0, 2);
const stop = (e: MouseEvent) => e.stopPropagation();

function Check({ on, mixed, label, onClick }: { on: boolean; mixed?: boolean; label: string; onClick: (e: MouseEvent) => void }) {
  return (
    <button type="button" role="checkbox" aria-checked={mixed ? 'mixed' : on} aria-label={label} className={s.cb} onClick={onClick}>
      <span className={`${s.box} ${on || mixed ? s.on : ''}`} aria-hidden="true">
        {on && <IconCheck size={12} stroke={3} />}
        {mixed && <span style={{ width: 8, height: 2, borderRadius: 1, background: 'currentColor' }} />}
      </span>
    </button>
  );
}

function Avatar({ ppl, name, size = 24 }: { ppl: string; name: string; size?: number }) {
  const photo = HAS_PHOTO[ppl];
  return (
    <span className={`${s.av} ${photo ? s.photo : ''}`} style={{ width: size, height: size }} title={photo ? name : `${name} · chưa có ảnh đại diện`} aria-hidden="true">
      {photo ? (
        <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
          <circle cx="20" cy="16" r="7.5" fill="currentColor" opacity="0.55" />
          <path d="M5 41c1.2-9.5 7.4-13.5 15-13.5S33.8 31.5 35 41z" fill="currentColor" />
        </svg>
      ) : (
        initials(name)
      )}
    </span>
  );
}

function FilterBtn({ label, icon, vals, cur, open, onToggle, onPick, onClear }: {
  label: string; icon: ReactNode; vals: readonly (readonly [string, string, number])[]; cur: readonly string[]; open: boolean; onToggle: () => void; onPick: (v: string) => void; onClear: () => void;
}) {
  const text = cur.length === 1 ? (vals.find((v) => v[0] === cur[0])?.[1] ?? cur[0]) : cur.length ? `${label} · ${cur.length}` : label;
  return (
    <div className={s.fwrap}>
      <button type="button" className={`${s.fbtn} ${cur.length ? s.on : ''}`} aria-haspopup="listbox" aria-expanded={open} title={label} onClick={onToggle}>
        {icon}
        <span>{text}</span>
        <IconChevronDown size={13} aria-hidden="true" />
      </button>
      {open && (
        <div role="listbox" aria-multiselectable="true" aria-label={label} className={s.pop}>
          {vals.map(([v, k, n]) => {
            const on = cur.includes(v);
            return (
              <button key={v} type="button" role="option" aria-selected={on} className={s.opt} onClick={() => onPick(v)}>
                <span className={`${s.box} ${on ? s.on : ''}`} aria-hidden="true">{on && <IconCheck size={12} stroke={3} />}</span>
                {k}
                <em>{n}</em>
              </button>
            );
          })}
          {cur.length > 0 && (
            <button type="button" className={s.selClr} onClick={onClear}>
              <IconFilterOff size={16} aria-hidden="true" />
              {tr('opsc.1')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function OpsCost() {
  const toast = useToast();
  const access = useFinanceAccess();
  const { condensed } = useShell();
  const pad = useLayout() === 'pad';
  const [tab, setTab] = useState<OpsTab>('row');
  const [per, setPer] = useState<Period>('w');
  const [off, setOff] = useState(0);
  const [dense, setDense] = useState(false);
  const [f, setF] = useState<OpsFilter>(NO_FILTER);
  const [pop, setPop] = useState<FKey | null>(null);
  const [domOpen, setDomOpen] = useState(false);
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [xc, setXc] = useState<Record<string, boolean>>({});
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });

  const state = financeScreenState(access);
  const mask = state === 'noperm';
  const ready = state === 'ready' || mask;
  const fk = (n: number) => (mask ? MASK : th(n));
  const ff = (n: number) => (mask ? '••••••' : fullVnd(n));

  const inPer = useMemo(() => (ready ? tripsInPeriod(per, off) : []), [ready, state, per, off]);
  const fil = useMemo(() => inPer.filter((t) => passFilter(t, f)), [inPer, f]);
  const anyF = f.ppl.length + f.veh.length + f.status.length > 0 || !!f.chip || !!f.q;
  const clearAll = () => { setF(NO_FILTER); setPop(null); };
  const lbl = periodLabel(per, off);
  const selIds = fil.filter((t) => sel[t.id]).map((t) => t.id);
  const allOn = fil.length > 0 && selIds.length === fil.length;
  const pendSel = fil.filter((t) => sel[t.id] && t.status === 'apv').length;
  const sumTrips = (ts: readonly OpsTrip[]) => ts.reduce((a, t) => a + t.tot, 0);

  const uniq = (xs: string[]) => xs.filter((x, i) => xs.indexOf(x) === i);
  const fbtns: { key: FKey; label: string; icon: ReactNode; vals: [string, string, number][] }[] = [
    { key: 'ppl', label: 'Tài xế', icon: <IconBuilding size={15} aria-hidden="true" />, vals: uniq(inPer.map((t) => t.ppl)).map((p) => [p, inPer.find((t) => t.ppl === p)!.name, inPer.filter((t) => t.ppl === p).length]) },
    { key: 'veh', label: 'Xe', icon: <IconTruck size={15} aria-hidden="true" />, vals: uniq(inPer.map((t) => t.veh)).map((v) => [v, v, inPer.filter((t) => t.veh === v).length]) },
    { key: 'status', label: 'Trạng thái', icon: <IconFilter size={15} aria-hidden="true" />, vals: (['pnd', 'apv', 'ok', 'set'] as const).map((k) => [k, STATUS_LABEL[k], inPer.filter((t) => t.status === k).length]) },
  ];
  const pick = (key: FKey, v: string) => setF((p) => ({ ...p, [key]: (p[key] as string[]).includes(v) ? (p[key] as string[]).filter((x) => x !== v) : [...(p[key] as string[]), v] }));
  const popPos = useMemo<CSSProperties>(() => ({ position: 'absolute', left: 0, top: 'calc(100% + 6px)', zIndex: 20 }), []);

  const cats = useMemo(() => catTotals(fil), [fil]);
  const drivers = useMemo(() => driverTotals(fil), [fil]);
  const tAct = cats.reduce((a, c) => a + c.act, 0);
  const tNorm = cats.reduce((a, c) => a + c.norm, 0);
  const tOver = cats.reduce((a, c) => a + c.over, 0);

  const meterOf = (act: number, norm: number) => {
    const mx = Math.max(act, norm, 1) * 1.08;
    return { bw: (act / mx) * 100, nx: (norm / mx) * 100, over: norm > 0 && act > norm * 1.05 + 0.5 };
  };
  const padBody = (): ReactNode => {
    if (tab === 'row') {
      return (
        <section role="table" aria-label="Theo chuyến" aria-rowcount={fil.length} className={p.list}>
          {fil.map((t) => {
            const on = !!sel[t.id];
            const ov = t.lines.filter((x) => overInCols(t, [x.cat]).includes(x));
            return (
              <div key={t.id} role="row" aria-selected={on} className={p.row}>
                <Check on={on} label={`Chọn ${t.id}`} onClick={(e) => { e.stopPropagation(); setSel((q) => ({ ...q, [t.id]: !on })); }} />
                <button type="button" className={p.open} title={`${t.id} · ${STATUS_LABEL[t.status]}`} onClick={() => setOpenId(t.id)}>
                  <Avatar ppl={t.ppl} name={t.name} size={34} />
                  <span className={p.mid}>
                    <span className={p.l1}><b>{t.id}</b><span className={p.sub}>{t.name} · {t.veh}</span></span>
                    <span className={p.l2}>{t.from} → {t.to}</span>
                  </span>
                  <span className={p.end}>
                    <b>{fk(t.tot)}</b>
                    <span className={p.flags}>
                      {ov.length > 0 && <span role="img" className={s.warnC} aria-label={overTip(ov)} title={overTip(ov)}><IconAlertTriangle size={14} aria-hidden="true" /></span>}
                      {t.vMiss > 0 && <span role="img" className={s.errC} aria-label={`${t.vMiss} lượt chưa có trong sao kê VETC`} title={`${t.vMiss} lượt chưa có trong sao kê VETC`}><IconTicket size={14} aria-hidden="true" /></span>}
                      <span className={p.dot} style={{ background: STATUS_DOT[t.status] }} role="img" aria-label={STATUS_LABEL[t.status]} title={STATUS_LABEL[t.status]} />
                    </span>
                  </span>
                </button>
              </div>
            );
          })}
        </section>
      );
    }
    if (tab === 'cat') {
      return (
        <section role="table" aria-label="Theo loại phí" className={p.list}>
          {cats.map((c) => (
            <div key={c.key} role="row" className={p.row}>
              <button type="button" className={p.open} title={c.codes.join(' · ')} onClick={() => toast({ msg: 'Chi tiết loại phí chưa mở ở bản này' })}>
                <span className={p.ic} aria-hidden="true"><IconReceipt size={18} /></span>
                <span className={p.mid}><span className={p.l1}><b>{c.name}</b></span><span className={p.l2}>{c.codes.join(' · ')}</span></span>
                <span className={p.end}><b>{fk(c.act)}</b><span className={p.l2}>{c.over > 0 ? `${c.over} vượt` : `${c.n} dòng`}</span></span>
              </button>
            </div>
          ))}
        </section>
      );
    }
    return (
      <section role="table" aria-label="Theo tài xế" className={p.list}>
        {drivers.map((d) => (
          <div key={d.ppl} role="row" className={p.row}>
            <button type="button" className={p.open} title={d.bal >= 0 ? `Tạm ứng thừa · nộp lại ${ff(d.bal)}` : `Chi vượt tạm ứng · công ty hoàn ${ff(-d.bal)}`} onClick={() => toast({ msg: 'Quyết toán chưa mở ở bản này' })}>
              <Avatar ppl={d.ppl} name={d.name} size={34} />
              <span className={p.mid}><span className={p.l1}><b>{d.name}</b><span className={p.sub}>{d.veh}</span></span><span className={p.l2}>{d.n} chuyến · hạn {d.dueLabel}</span></span>
              <span className={p.end}><b className={d.bal >= 0 ? undefined : s.linkC}>{mask ? MASK : d.bal >= 0 ? `Nộp lại ${th(d.bal)}` : `Hoàn ứng ${th(-d.bal)}`}</b></span>
            </button>
          </div>
        ))}
      </section>
    );
  };
  const overview = (): ReactNode => (
    <aside className={p.right} aria-label="Tổng quan kỳ">
      <h2><IconCoin size={17} aria-hidden="true" />{tr('opsc.2')}</h2>
      {ready && fil.length > 0 && (
        <>
          <div className={p.tiles}>
            {([['Thực tế', ff(tAct), ''], ['Định mức', ff(tNorm), s.tx2C], ['Dòng vượt', String(tOver), tOver ? s.warnC : '']] as const).map(([k, v, c]) => (
              <div key={k} className={p.tile} title={k}><span>{k}</span><b className={c}>{v}</b></div>
            ))}
          </div>
          <div className={p.grp}>
            <div className={p.gh}><IconExternalLink size={15} aria-hidden="true" />{tr('opsc.3')}</div>
            {cats.filter((c) => c.n > 0).map((c) => {
              const m = meterOf(c.act, c.norm);
              const tip = `Thực tế ${ff(c.act)} · định mức ${ff(c.norm)}`;
              return (
                <button key={c.key} type="button" className={p.bar} title={tip} onClick={() => { setTab('cat'); setXc((q) => ({ ...q, [c.key]: true })); }}>
                  <span className={p.bk}>{c.name}</span>
                  <span role="img" aria-label={tip} className={p.track}>
                    <i className={m.over ? p.over : ''} style={{ width: `${m.bw}%` }} />
                    {c.norm > 0 && <u style={{ left: `${m.nx}%` }} />}
                  </span>
                  <b className={m.over ? s.warnC : undefined}>{fk(c.act)}</b>
                </button>
              );
            })}
          </div>
          {drivers.length > 0 && (
            <div className={p.grp}>
              <div className={p.gh}><IconCash size={15} aria-hidden="true" />{tr('opsc.4')}</div>
              {drivers.map((d) => (
                <button key={d.ppl} type="button" className={p.bar} title={d.bal >= 0 ? `Tạm ứng thừa · nộp lại ${ff(d.bal)}` : `Chi vượt tạm ứng · công ty hoàn ${ff(-d.bal)}`} onClick={() => { setTab('ppl'); }}>
                  <Avatar ppl={d.ppl} name={d.name} size={28} />
                  <b className={p.dn}>{d.name}</b>
                  <span className={`${p.due} ${d.late ? s.errC : d.dueSoon ? s.warnC : ''}`} title={`Hạn quyết toán ${String(d.due).padStart(2, '0')}/Oct/2026`}>{d.dueLabel}</span>
                  <b className={d.bal >= 0 ? undefined : s.linkC}>{mask ? MASK : d.bal >= 0 ? `Nộp lại ${th(d.bal)}` : `Hoàn ứng ${th(-d.bal)}`}</b>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </aside>
  );

  const body = (): ReactNode => {
    if (state === 'loading') return <div className={s.card}><Skeleton rows={8} /></div>;
    if (state === 'empty') return <div className={s.card}><StateView state="empty" title="Chưa có chi phí trong kỳ" /></div>;
    if (state === 'error') {
      return (
        <div className={s.card}>
          <StateView state="error" title="Không tải được dữ liệu. Đang hiện bản lưu gần nhất." action={<Button onClick={() => toast({ msg: 'Đang tải lại…' })}>{tr('opsc.5')}</Button>} />
        </div>
      );
    }
    if (!fil.length) {
      return (
        <div className={s.card}>
          <div className={s.none}>
            {tr('opsc.6')}
            <button type="button" className={s.ghost} onClick={clearAll}>
              <IconFilterOff size={16} aria-hidden="true" />
              {tr('opsc.1')}
            </button>
          </div>
        </div>
      );
    }
    if (pad) return padBody();
    if (tab === 'row') {
      return (
        <section role="table" aria-label="Chi phí theo chuyến" aria-rowcount={fil.length} className={`${s.card} ${dense ? s.dense : ''}`}>
          {fil.map((t) => {
            const on = !!sel[t.id];
            return (
              <div
                key={t.id}
                role="row"
                tabIndex={0}
                aria-selected={on}
                className={s.row}
                style={{ gridTemplateColumns: GRID }}
                title={`${t.id} · ${STATUS_LABEL[t.status]}`}
                onClick={() => setOpenId(t.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') setOpenId(t.id); }}
              >
                <div role="cell" className={s.cc}>
                  <Check on={on} label={`Chọn ${t.id}`} onClick={(e) => { e.stopPropagation(); setSel((p) => ({ ...p, [t.id]: !on })); }} />
                </div>
                <div role="cell" className={`${s.c} ${s.id}`} title={`Job ${t.job} · ${t.cont} · ${dayLabel(t.day)}/2026`}>
                  <b>{t.id}</b>
                  <span className={s.dd}>{dayLabel(t.day)}</span>
                </div>
                <div role="cell" className={s.c}>
                  <Avatar ppl={t.ppl} name={t.name} />
                  <span className={s.ell} style={{ fontWeight: 'var(--fw-label)' }}>{t.name}</span>
                </div>
                <div role="cell" className={`${s.c} ${s.veh} ${s.ell}`}>{t.veh}</div>
                <div role="cell" className={`${s.c} ${s.route}`} title={t.stops.join(' → ')}>
                  <span className={s.cg} style={{ background: CARGO_DOT[t.cg] }} aria-hidden="true" title={CARGO_LABEL[t.cg]} />
                  <span className={s.from}>{t.from}</span>
                  <IconArrowRight size={14} aria-hidden="true" />
                  <b className={s.ell}>{t.to}</b>
                  {t.extra > 0 && <span className={s.chen} title={`${t.extra} điểm chen`}>+{t.extra}</span>}
                </div>
                {COLS.map((c) => {
                  const v = sumCats(t, c.cats);
                  const ov = overInCols(t, c.cats);
                  return (
                    <div key={c.key} role="cell" className={`${s.c} ${s.amt} ${ov.length ? s.warnC : v ? '' : s.lbC}`} title={`${c.label} · ${v ? ff(v) : 'không có'}`}>
                      {ov.length > 0 && (
                        <button type="button" className={s.flag} aria-label={overTip(ov)} title={overTip(ov)} onClick={(e) => { stop(e); toast({ msg: 'Định mức chưa mở ở bản này' }); }}>
                          <IconAlertTriangle size={14} aria-hidden="true" />
                        </button>
                      )}
                      {c.key === 'toll' && t.vMonth > 0 && (
                        <span role="img" className={`${s.flag} ${s.linkC}`} aria-label={`${t.vMonth} lượt trạm vé tháng · không có trong sao kê`} title={`${t.vMonth} lượt trạm vé tháng · không có trong sao kê`}>
                          <IconTicket size={14} aria-hidden="true" />
                        </span>
                      )}
                      {c.key === 'toll' && t.vMiss > 0 && (
                        <span role="img" className={`${s.flag} ${s.errC}`} aria-label={`${t.vMiss} lượt chưa có trong sao kê VETC`} title={`${t.vMiss} lượt chưa có trong sao kê VETC`}>
                          <IconAlertTriangle size={14} aria-hidden="true" />
                        </span>
                      )}
                      <span>{v ? fk(v) : '—'}</span>
                    </div>
                  );
                })}
                <div role="cell" className={`${s.c} ${s.tot}`} title={`Tổng chi · ${ff(t.tot)}`}>{fk(t.tot)}</div>
                <div role="cell" className={`${s.c} ${s.st}`} title={STATUS_LABEL[t.status]}>
                  <span className={s.dot} style={{ background: STATUS_DOT[t.status] }} aria-hidden="true" />
                  <span className={`${s.ell} ${t.status === 'pnd' ? s.warnC : t.status === 'set' ? s.lbC : ''}`}>{STATUS_LABEL[t.status]}</span>
                </div>
              </div>
            );
          })}
          <div role="row" className={s.foot} style={{ gridTemplateColumns: GRID }}>
            <div role="cell" />
            <div role="cell" className={s.tx2C} style={{ gridColumn: 'span 4' }}>{fil.length} chuyến</div>
            {COLS.map((c) => (
              <div key={c.key} role="cell" className={s.r}>{fk(fil.reduce((a, t) => a + sumCats(t, c.cats), 0))}</div>
            ))}
            <div role="cell" className={s.r} title={`Tổng chi ${fil.length} chuyến`}>{fk(sumTrips(fil))}</div>
            <div role="cell" />
          </div>
        </section>
      );
    }
    const meter = (act: number, norm: number) => {
      const mx = Math.max(act, norm, 1) * 1.08;
      const over = norm > 0 && act > norm * 1.05 + 0.5;
      return (
        <span role="img" className={s.meter} aria-label={`Thực tế ${ff(act)} · định mức ${ff(norm)}`} title={`Thực tế ${ff(act)} · định mức ${ff(norm)}`}>
          <i className={over ? s.over : ''} style={{ width: `${(act / mx) * 100}%` }} />
          {norm > 0 && <u style={{ left: `${(norm / mx) * 100}%` }} />}
        </span>
      );
    };
    const diff = (act: number, norm: number) => {
      const d = act - norm;
      return <span className={d > norm * 0.05 + 0.5 ? s.warnC : d < 0 ? s.okC : s.tx2C} style={{ fontWeight: 'var(--fw-label)' }}>{mask ? MASK : d === 0 ? '0' : (d > 0 ? '+' : '−') + th(Math.abs(d))}</span>;
    };
    const tiles = (items: readonly (readonly [string, string, string | undefined, ReactNode])[]) => (
      <div className={s.tiles}>
        {items.map(([k, v, c, ic]) => (
          <div key={k} className={s.tile} title={k}>
            <div>{ic}{k}</div>
            <b className={c}>{v}</b>
          </div>
        ))}
      </div>
    );
    if (tab === 'cat') {
      const lines = fil.flatMap((t) => t.lines);
      return (
        <div className={s.body}>
          {tiles([
            ['Thực tế', ff(tAct), '', <IconCoin key="a" size={14} aria-hidden="true" />],
            ['Định mức', ff(tNorm), s.tx2C, <IconExternalLink key="b" size={14} aria-hidden="true" />],
            ['Chênh', mask ? '••••••' : `${tAct - tNorm >= 0 ? '+' : '−'}${th(Math.abs(tAct - tNorm) * 1000)} ₫`, tAct > tNorm ? s.warnC : s.okC, <IconAlertTriangle key="c" size={14} aria-hidden="true" />],
            ['Dòng vượt định mức', String(tOver), tOver ? s.warnC : '', <IconAlertTriangle key="d" size={14} aria-hidden="true" />],
          ])}
          <section role="table" aria-label="Theo loại phí" className={`${s.tbl} ${dense ? s.dense : ''}`}>
            <div role="row" className={`${s.th} ${s.catG}`}>
              {['Loại phí', 'Dòng', 'Định mức', 'Thực tế', 'So định mức', 'Chênh', 'Vượt', ''].map((k, i) => (
                <div key={k + i} role="columnheader" className={i === 1 || i === 2 || i === 3 || i === 5 || i === 6 ? s.r : ''}>{k}</div>
              ))}
            </div>
            {cats.map((c) => {
              const open = !!xc[c.key];
              const sub = c.codes.map((code) => {
                const l2 = lines.filter((x) => x.cat === c.key && x.code === code);
                return { code, n: l2.length, act: l2.reduce((a, x) => a + x.amt, 0), norm: l2.reduce((a, x) => a + (x.norm == null ? x.amt : x.norm), 0) };
              }).filter((x) => x.n > 0);
              return (
                <div key={c.key} style={{ borderBottom: '1px solid var(--line)' }}>
                  <div role="row" className={`${s.rowH} ${s.click} ${s.catG}`} style={{ background: open ? 'var(--sub)' : undefined }} onClick={() => setXc((p) => ({ ...p, [c.key]: !open }))}>
                    <div role="cell" className={s.c} style={{ gap: 10 }}>
                      <button type="button" className={s.tg} aria-expanded={open} aria-label={(open ? 'Thu gọn ' : 'Mở ') + c.name} onClick={(e) => { stop(e); setXc((p) => ({ ...p, [c.key]: !open })); }}>
                        <IconChevronRight size={14} aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : undefined }} />
                      </button>
                      <span className={s.ic} aria-hidden="true"><IconReceipt size={16} /></span>
                      <span className={s.ell}><b style={{ fontWeight: 'var(--fw-label)' }}>{c.name}</b><br /><span className={s.dd}>{c.codes.join(' · ')}</span></span>
                    </div>
                    <div role="cell" className={`${s.r} ${s.tx2C}`}>{c.n}</div>
                    <div role="cell" className={`${s.r} ${s.tx2C}`}>{fk(c.norm)}</div>
                    <div role="cell" className={s.r} style={{ fontWeight: 600 }}>{fk(c.act)}</div>
                    <div role="cell" className={s.c} style={{ padding: '0 12px' }}>{meter(c.act, c.norm)}</div>
                    <div role="cell" className={s.r}>{diff(c.act, c.norm)}</div>
                    <div role="cell" className={s.r}>{c.over > 0 && <span className={s.pill} title={`${c.over} dòng vượt định mức`}><IconAlertTriangle size={12} aria-hidden="true" />{c.over}</span>}</div>
                    <div role="cell" className={s.cc}>
                      <button type="button" className={s.nrm} aria-label={`Định mức ${c.name}`} title={`Định mức ${c.name}`} onClick={(e) => { stop(e); toast({ msg: 'Định mức chưa mở ở bản này' }); }}>
                        <IconExternalLink size={15} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  {open && (
                    <div style={{ padding: '4px 8px 14px' }}>
                      {sub.map((x) => (
                        <div key={x.code} role="row" className={`${s.rowH} ${s.sub} ${s.catG}`}>
                          <div role="cell" className={s.c} style={{ paddingLeft: 82 }}><span className={s.code}>{x.code}</span><span className={`${s.ell} ${s.tx2C}`}>{FEE_NAME[x.code] ?? x.code}</span></div>
                          <div role="cell" className={`${s.r} ${s.tx2C}`}>{x.n}</div>
                          <div role="cell" className={`${s.r} ${s.tx2C}`}>{fk(x.norm)}</div>
                          <div role="cell" className={s.r} style={{ fontWeight: 'var(--fw-label)' }}>{fk(x.act)}</div>
                          <div role="cell" className={s.c} style={{ padding: '0 12px' }}>{meter(x.act, x.norm)}</div>
                          <div role="cell" className={s.r}>{diff(x.act, x.norm)}</div>
                          <div role="cell" /><div role="cell" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        </div>
      );
    }
    const pAdv = drivers.reduce((a, d) => a + d.adv, 0);
    const pSpent = fil.filter((t) => t.status !== 'set').reduce((a, t) => a + t.emp, 0);
    const pPend = fil.filter((t) => t.status === 'apv' || t.status === 'pnd').reduce((a, t) => a + t.emp, 0);
    const nLate = drivers.filter((d) => d.late).length;
    return (
      <div className={s.body}>
        {tiles([
          ['Tạm ứng đang giữ', ff(pAdv), '', <IconCash key="a" size={14} aria-hidden="true" />],
          ['Đã chi', ff(pSpent), '', <IconCoin key="b" size={14} aria-hidden="true" />],
          ['Chờ duyệt', ff(pPend), s.warnC, <IconClock key="c" size={14} aria-hidden="true" />],
          ['Quá hạn quyết toán', String(nLate), nLate ? s.errC : '', <IconLock key="d" size={14} aria-hidden="true" />],
        ])}
        <section role="table" aria-label="Theo tài xế" className={`${s.tbl} ${dense ? s.dense : ''}`}>
          <div role="row" className={`${s.th} ${s.drvG}`}>
            {['Tài xế', 'Xe', 'Chuyến', 'Tạm ứng giữ', 'Đã chi', 'Chờ duyệt', 'Cần quyết toán', 'Số dư', 'Hạn', ''].map((k, i) => (
              <div key={k + i} role="columnheader" className={i >= 2 && i <= 8 ? s.r : ''}>{k}</div>
            ))}
          </div>
          {drivers.map((d) => (
            <div key={d.ppl} role="row" className={`${s.rowH} ${s.drvG}`} style={{ borderBottom: '1px solid var(--line)' }}>
              <div role="cell" className={s.c} style={{ gap: 10 }}><Avatar ppl={d.ppl} name={d.name} size={30} /><b style={{ fontWeight: 'var(--fw-label)' }}>{d.name}</b></div>
              <div role="cell" className={s.tx2C}>{d.veh}</div>
              <div role="cell" className={`${s.r} ${s.tx2C}`}>{d.n}</div>
              <div role="cell" className={s.r} style={{ fontWeight: 'var(--fw-label)' }}>{fk(d.adv)}</div>
              <div role="cell">
                <div className={s.bal}>
                  <span className={s.r} style={{ fontWeight: 'var(--fw-label)' }}>{fk(d.spent)}</span>
                  <i role="img" aria-label={`Đã chi ${ff(d.spent)} / tạm ứng ${ff(d.adv)}`} title={`Đã chi ${ff(d.spent)} / tạm ứng ${ff(d.adv)}`}><u className={d.spent > d.adv ? s.over : ''} style={{ width: `${Math.min(100, d.adv ? (d.spent / d.adv) * 100 : 100)}%` }} /></i>
                </div>
              </div>
              <div role="cell" className={`${s.r} ${d.pend ? s.warnC : s.lbC}`}>{d.pend ? fk(d.pend) : '—'}</div>
              <div role="cell" className={s.r} style={{ fontWeight: 600 }}>{d.need ? fk(d.need) : '—'}</div>
              <div role="cell" className={`${s.r} ${d.bal >= 0 ? s.tx2C : s.linkC}`} style={{ fontWeight: 'var(--fw-label)' }} title={d.bal >= 0 ? `Tạm ứng thừa · nộp lại ${ff(d.bal)}` : `Chi vượt tạm ứng · công ty hoàn ${ff(-d.bal)}`}>
                {mask ? MASK : d.bal >= 0 ? `Nộp lại ${th(d.bal)}` : `Hoàn ứng ${th(-d.bal)}`}
              </div>
              <div role="cell" className={`${s.c} ${s.amt} ${d.late ? s.errC : d.dueSoon ? s.warnC : s.tx2C}`} title={`Hạn quyết toán ${String(d.due).padStart(2, '0')}/Oct/2026${d.late ? ' · quá hạn, không ứng mới' : ''}`}>
                <IconClock size={13} aria-hidden="true" />
                <span style={{ fontWeight: 'var(--fw-label)' }}>{d.dueLabel}</span>
              </div>
              <div role="cell" className={s.c} style={{ justifyContent: 'flex-end' }}>
                <button type="button" className={s.open} onClick={() => toast({ msg: 'Quyết toán chưa mở ở bản này' })}>
                  <IconExternalLink size={16} aria-hidden="true" />
                  {tr('opsc.7')}
                </button>
              </div>
            </div>
          ))}
        </section>
      </div>
    );
  };

  return (
    <Page kind="table">
      <PageHeader
        // PageHeader nhận `title: string` nhưng render thẳng vào <h1>; board đặt nút chọn mảng trong tiêu đề → truyền nút (đề nghị luồng A mở kiểu ReactNode).
        title={
          (
            <span className={`${s.title} ${condensed ? s.titleCond : ''} ${pad ? p.titleFix : ''}`}>
              {tr('opsc.8')}
              <span aria-hidden="true" className={s.sep}>·</span>
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <button type="button" className={s.dom} aria-haspopup="listbox" aria-expanded={domOpen} aria-label="Chọn mảng · Trucking" title="Chọn mảng" onClick={() => setDomOpen((v) => !v)}>
                  Trucking
                  <IconChevronDown size={16} aria-hidden="true" />
                </button>
                {domOpen && (
                  <>
                    <div aria-hidden="true" onClick={() => setDomOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 19 }} />
                    <div role="listbox" aria-label="Mảng" className={s.pop} style={{ ...popPos, width: 280 }}>
                      {['Trucking', 'Hải quan', 'Kiểm dịch / C/O', 'Hun trùng', 'Cảng / depot'].map((k, i) => (
                        <button key={k} type="button" role="option" aria-selected={i === 0} className={s.opt} onClick={() => { setDomOpen(false); if (i) toast({ msg: 'Mảng này chưa mở ở bản này' }); }}>
                          {k}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </span>
            </span>
          ) as unknown as string
        }
        findHint={false}
        tabs={
          <div className={s.hd}>
            <div className={s.seg} role="group" aria-label="Kỳ">
              {([['w', 'Tuần'], ['m', 'Tháng']] as const).map(([k, l]) => (
                <button key={k} type="button" aria-pressed={per === k} onClick={() => { setPer(k); setOff(0); setSel({}); }}>{l}</button>
              ))}
            </div>
            <div className={s.stepper}>
              <button type="button" className={s.step} aria-label="Kỳ trước" title="Kỳ trước" onClick={() => { setOff(off - 1); setSel({}); }}>
                <IconChevronLeft size={16} aria-hidden="true" />
              </button>
              <span className={s.per} title={lbl.tip}>
                <IconCalendar size={15} aria-hidden="true" />
                {lbl.k}
              </span>
              <button type="button" className={s.step} aria-label="Kỳ sau" title="Kỳ sau" aria-disabled={off >= 0} onClick={() => { if (off >= 0) toast({ msg: 'Đang ở kỳ hiện tại' }); else { setOff(off + 1); setSel({}); } }}>
                <IconChevronRight size={16} aria-hidden="true" />
              </button>
            </div>
            <div className={pad ? p.chips : p.flat}>
            {CHIPS.map((c) => {
              const n = inPer.filter((t) => chipPass(t, c.k)).length;
              const on = f.chip === c.k;
              return (
                <button key={c.k} type="button" className={`${s.chip} ${c.k === 'over' && n > 0 && !on ? s.warn : ''}`} aria-pressed={on} title={`${c.tip} · ${n} chuyến`} onClick={() => { setF((p) => ({ ...p, chip: on ? '' : c.k })); setSel({}); }}>
                  {c.ic}
                  <b aria-label={`${c.tip} · ${n} chuyến`}>{n}</b>
                  {pad && c.short}
                </button>
              );
            })}
            </div>
            <span className={s.spacer} />
            <button type="button" className={s.tool} aria-label="Nhập sao kê VETC" title="Nhập sao kê VETC" onClick={() => toast({ msg: 'Nhập sao kê chưa mở ở bản này' })}>
              <IconUpload size={16} stroke={1.8} aria-hidden="true" />
            </button>
            <button type="button" className={s.tool} aria-label="Xuất Excel" title="Xuất Excel" onClick={() => toast({ msg: 'Chưa mở xuất Excel ở bản này' })}>
              <IconDownload size={16} stroke={1.8} aria-hidden="true" />
            </button>
            <button type="button" className={s.tool} aria-label="Lịch sử" title="Lịch sử" onClick={() => toast({ msg: 'Lịch sử chưa mở ở bản này' })}>
              <IconHistory size={16} stroke={1.8} aria-hidden="true" />
            </button>
            <Button variant="primary" className={s.apv} icon={<IconCheck size={16} stroke={1.8} aria-hidden="true" />} title={selIds.length ? `Duyệt ${pendSel} chuyến đã chọn` : 'Duyệt mọi chuyến chờ duyệt đang hiện'} onClick={noWrite}>
              {tr('opsc.9')}
            </Button>
          </div>
        }
      />
      <div className={`${s.page} ${pad ? p.page : ''}`}>
        <div className={`${s.stick} ${pad ? p.stick : ''}`}>
          <div className={s.bar}>
            <div className={`${s.seg} ${s.segTab}`} role="tablist" aria-label="Cách xem">
              {TABS.map(([k, l]) => (
                <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => { setTab(k); setPop(null); }}>{l}</button>
              ))}
            </div>
            <label className={s.q}>
              <IconSearch size={15} aria-hidden="true" />
              <input aria-label="Lệnh, job, tài xế, xe" placeholder="Lệnh, job, tài xế, xe" value={f.q} onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))} />
              {f.q && (
                <button type="button" className={s.qx} aria-label="Xoá ô tìm" title="Xoá ô tìm" onClick={() => setF((p) => ({ ...p, q: '' }))}>
                  <IconX size={14} aria-hidden="true" />
                </button>
              )}
            </label>
            {fbtns.map((b) => (
              <FilterBtn key={b.key} label={b.label} icon={b.icon} vals={b.vals} cur={f[b.key] as string[]} open={pop === b.key} onToggle={() => setPop(pop === b.key ? null : b.key)} onPick={(v) => pick(b.key, v)} onClear={() => { setF((p) => ({ ...p, [b.key]: [] })); setPop(null); }} />
            ))}
            {anyF && (
              <button type="button" className={s.clr} aria-label="Bỏ mọi lọc" title="Bỏ mọi lọc" onClick={clearAll}>
                <IconFilterOff size={16} aria-hidden="true" />
              </button>
            )}
            <span className={s.spacer} />
            {ready && !pad && (
              <span className={s.cnt} title={`${fil.length} khớp lọc / ${inPer.length} trong kỳ · số tiền tính bằng nghìn ₫`}>{fil.length} / {inPer.length} chuyến · nghìn ₫</span>
            )}
            {!pad && <div className={s.seg} role="group" aria-label="Mật độ">
              {([['Chuẩn', false], ['Gọn', true]] as const).map(([k, v]) => (
                <button key={k} type="button" aria-pressed={dense === v} onClick={() => setDense(v)}>{k}</button>
              ))}
            </div>}
          </div>
          {tab === 'row' && selIds.length > 0 && ready && (
            <div role="toolbar" aria-label={`${selIds.length} đã chọn`} className={s.sel}>
              <b>{selIds.length} đã chọn</b>
              <button type="button" className={s.selClr} onClick={() => setSel({})}>
                <IconFilterOff size={16} aria-hidden="true" />
                {tr('opsc.10')}
              </button>
              <span className={s.spacer} />
              <b style={{ color: 'var(--tx)' }} title="Tổng chi các dòng đã chọn">{ff(sumTrips(fil.filter((t) => sel[t.id])))}</b>
              <button type="button" className={s.bulk} aria-label="Nhắc chứng từ" title="Nhắc chứng từ" onClick={noWrite}><IconBell size={16} aria-hidden="true" /></button>
              <button type="button" className={s.bulk} aria-label="Xuất Excel đã chọn" title="Xuất Excel" onClick={() => toast({ msg: 'Chưa mở xuất Excel ở bản này' })}><IconDownload size={16} aria-hidden="true" /></button>
              <button type="button" className={s.bulkOk} onClick={noWrite}>{pendSel ? `Duyệt ${pendSel}` : 'Duyệt'}</button>
            </div>
          )}
          {mask && (
            <div role="status" className={s.note}>
              <IconLock size={14} aria-hidden="true" />
              {tr('opsc.11')}
            </div>
          )}
          {tab === 'row' && pad && ready && fil.length > 0 && (
            <div className={p.all}>
              <Check on={allOn} mixed={!allOn && selIds.length > 0} label="Chọn mọi chuyến đang hiện" onClick={() => setSel(allOn ? {} : Object.fromEntries(fil.map((t) => [t.id, true])))} />
<span title={`${fil.length} khớp lọc / ${inPer.length} trong kỳ · số tiền tính bằng nghìn ₫`}>{fil.length} / {inPer.length} chuyến · nghìn ₫</span>
              <span className={s.spacer} />
              <b className={p.sum} title={`Tổng chi ${fil.length} chuyến`}>{ff(sumTrips(fil))}</b>
            </div>
          )}
          {tab === 'row' && !pad && ready && fil.length > 0 && (
            <div role="row" className={`${s.th} ${dense ? s.dense : ''}`} style={{ gridTemplateColumns: GRID, height: dense ? 37 : 41 }}>
              <div role="columnheader" className={s.cc} style={{ padding: 0 }}>
                <Check on={allOn} mixed={!allOn && selIds.length > 0} label="Chọn mọi chuyến đang hiện" onClick={() => setSel(allOn ? {} : Object.fromEntries(fil.map((t) => [t.id, true])))} />
              </div>
              {TH_COLS.map(([k, right, tip]) => (
                <div key={k} role="columnheader" title={tip || k} className={right ? s.r : ''}>{k}</div>
              ))}
            </div>
          )}
        </div>
        {body()}
        {pad && overview()}
      </div>
      <OpsTripDrawer trip={ALL_TRIPS.find((x) => x.id === openId) ?? null} mask={mask} onClose={() => setOpenId(null)} />
    </Page>
  );
}
